/**
 * Auth store
 *
 * Manages:
 * - Authentication status and current user
 * - JWT token persistence
 * - Logout flow
 */

import { resetPushDevice } from '@/features/notifications/device'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useClubStore } from './club'
import { useNavigatorStore } from './navigator'

const MASQUERADE_ORIGINAL_USER_KEY = 'masqueradeOriginalUser'
const MASQUERADE_ORIGINAL_TOKEN_KEY = 'masqueradeOriginalToken'
const MASQUERADE_RETURN_KEY='masqueradeReturnPath'
export const DEMO_MASQUERADE_RETURN_PATH='/sysadmin/platform-maintenance?section=volleyball-acceptance'
const safeMasqueradeReturn=(value:string|null|undefined)=>typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !/[\\\u0000-\u0020]/.test(value) ? value : '/'
const PARTICIPANT_LIST_ACTIVE_SESSION_STORAGE_KEY = 'participantList.activeSession'

export interface AuthUser {
  id: string
  email: string
  name: string
  role?: string
  details?: Record<string, unknown>
}

const readStoredUser = (): AuthUser | null => {
  const storedUser = localStorage.getItem('user')
  if (!storedUser || storedUser === 'undefined' || storedUser === 'null') return null

  try {
    const user = JSON.parse(storedUser) as AuthUser
    return user && user.id && user.email ? user : null
  } catch {
    localStorage.removeItem('user')
    return null
  }
}

const readStoredMasqueradeOriginalUser = (): AuthUser | null => {
  const storedUser = localStorage.getItem(MASQUERADE_ORIGINAL_USER_KEY)
  if (!storedUser || storedUser === 'undefined' || storedUser === 'null') return null

  try {
    const user = JSON.parse(storedUser) as AuthUser
    return user && user.id && user.email ? user : null
  } catch {
    localStorage.removeItem(MASQUERADE_ORIGINAL_USER_KEY)
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(readStoredUser())
  const masqueradeOriginalUser = ref<AuthUser | null>(readStoredMasqueradeOriginalUser())
  const masqueradeReturnPath=ref(safeMasqueradeReturn(localStorage.getItem(MASQUERADE_RETURN_KEY)))
  const isAuthenticated = computed(() => !!user.value)
  const isMasquerading = computed(() => !!masqueradeOriginalUser.value)
  const token = ref(localStorage.getItem('token') || '')
  const switchingIdentity = ref(false)

  const setUser = (newUser: AuthUser) => {
    if (user.value && user.value.id !== newUser.id) resetPushDevice(token.value)
    localStorage.setItem('user', JSON.stringify(newUser))
    user.value = newUser
  }

  const setToken = (newToken: string) => {
    localStorage.setItem('token', newToken)
    localStorage.setItem('jwt', newToken)
    token.value = newToken
  }

  const setSession = (newToken: string, newUser: AuthUser) => {
    if (user.value && user.value.id !== newUser.id) resetPushDevice(token.value)
    // Synchronous identity watchers read both persistence and reactive state.
    // Publish only after the complete persisted session is ready; intermediate
    // ref notifications then fail closed until the final coherent notification.
    localStorage.setItem('token', newToken)
    localStorage.setItem('jwt', newToken)
    localStorage.setItem('user', JSON.stringify(newUser))
    user.value = newUser
    token.value = newToken
  }

  const beginMasquerade = (newToken: string, newUser: AuthUser, returnPath?:string) => {
    if (!masqueradeOriginalUser.value && user.value) {
      localStorage.setItem(MASQUERADE_ORIGINAL_USER_KEY, JSON.stringify(user.value))
      localStorage.setItem(MASQUERADE_ORIGINAL_TOKEN_KEY, token.value)
      masqueradeOriginalUser.value = user.value
      masqueradeReturnPath.value=safeMasqueradeReturn(returnPath)
      localStorage.setItem(MASQUERADE_RETURN_KEY,masqueradeReturnPath.value)
    }
    setSession(newToken, newUser)
    useClubStore().clearClubState()
    useNavigatorStore().clearNavigationState()
    clearEventScopedState()
  }

  const stopMasquerade = () => {
    const originalUser = masqueradeOriginalUser.value
    const originalToken = localStorage.getItem(MASQUERADE_ORIGINAL_TOKEN_KEY)

    if (!originalUser || !originalToken) return false

    localStorage.removeItem(MASQUERADE_ORIGINAL_USER_KEY)
    localStorage.removeItem(MASQUERADE_ORIGINAL_TOKEN_KEY)
    localStorage.removeItem(MASQUERADE_RETURN_KEY)
    masqueradeReturnPath.value='/'
    setSession(originalToken, originalUser)
    masqueradeOriginalUser.value = null
    useClubStore().clearClubState()
    useNavigatorStore().clearNavigationState()
    clearEventScopedState()
    return true
  }

  const logout = () => {
    resetPushDevice(token.value)
    localStorage.removeItem('token')
    localStorage.removeItem('jwt')
    localStorage.removeItem('user')
    localStorage.removeItem(MASQUERADE_ORIGINAL_USER_KEY)
    localStorage.removeItem(MASQUERADE_ORIGINAL_TOKEN_KEY)
    localStorage.removeItem(MASQUERADE_RETURN_KEY)
    masqueradeReturnPath.value='/'
    user.value = null
    token.value = ''
    masqueradeOriginalUser.value = null
    useClubStore().clearClubState()
    useNavigatorStore().clearNavigationState()
    clearEventScopedState()
  }

  const restoreSession = async () => {
    if (token.value) {
      try {
        // Try to validate token and restore user (implement via useAuth composable)
        return true
      } catch {
        logout()
        return false
      }
    }
    return false
  }

  return {
    user,
    masqueradeOriginalUser,
    masqueradeReturnPath,
    isAuthenticated,
    switchingIdentity,
    isMasquerading,
    token,
    setUser,
    setToken,
    setSession,
    beginMasquerade,
    stopMasquerade,
    logout,
    restoreSession,
  }
})

function clearEventScopedState() {
  localStorage.removeItem(PARTICIPANT_LIST_ACTIVE_SESSION_STORAGE_KEY)
}
